import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { useI18n } from '../utilities/i18n';

export default function ReorderableTable({
  columns,
  items,
  getRowKey,
  reorderMode,
  onItemsChange,
  onRowClick,
  getRowHref,
  isRowLocked,
  empty,
  tableClassName = '',
  sort,
  onSort,
}) {
  const { t } = useI18n();
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const dragIndexRef = useRef(null);

  const moveRow = (from, to) => {
    if (from === to || to < 0 || to >= items.length) return;
    const next = items.slice();
    const [row] = next.splice(from, 1);
    next.splice(to, 0, row);
    if (onItemsChange) onItemsChange(next);
  };

  const handleDragStart = (index, event) => {
    if (!reorderMode) return;
    dragIndexRef.current = index;
    setDragIndex(index);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(index));
  };

  const handleDragOver = (index, event) => {
    if (!reorderMode) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (overIndex !== index) setOverIndex(index);
  };

  const handleDrop = (index, event) => {
    if (!reorderMode) return;
    event.preventDefault();
    const from = dragIndexRef.current;
    if (from != null) moveRow(from, index);
    dragIndexRef.current = null;
    setDragIndex(null);
    setOverIndex(null);
  };

  const handleDragEnd = () => {
    dragIndexRef.current = null;
    setDragIndex(null);
    setOverIndex(null);
  };

  const handleHeaderClick = (column) => {
    if (!onSort || reorderMode || column.sortable === false) return;
    if (sort?.key === column.key) {
      onSort({ key: column.key, dir: sort.dir === 'asc' ? 'desc' : 'asc' });
      return;
    }
    onSort({ key: column.key, dir: 'asc' });
  };

  if (!items.length) {
    return empty || null;
  }

  return (
    <div className={`st-table ${reorderMode ? 'is-reorder' : ''} ${tableClassName}`.trim()}>
      <div className="st-table-header">
        {columns.map((column) => {
          const sortable = Boolean(onSort) && !reorderMode && column.sortable !== false;
          const active = sortable && sort?.key === column.key;
          return (
            <div key={column.key} className={`st-cell ${column.className || ''}`.trim()}>
              {sortable ? (
                <button
                  type="button"
                  className={`st-sort-btn ${active ? 'is-active' : ''}`.trim()}
                  onClick={() => handleHeaderClick(column)}
                >
                  <span>{column.label}</span>
                  {active ? (sort.dir === 'desc' ? <FiChevronDown /> : <FiChevronUp />) : null}
                </button>
              ) : column.label}
            </div>
          );
        })}
        {reorderMode ? <div className="st-cell st-cell-actions">{t('stdPriority')}</div> : null}
      </div>
      {items.map((item, index) => {
        const locked = Boolean(!reorderMode && isRowLocked && isRowLocked(item));
        const href = (!reorderMode && !locked && getRowHref) ? getRowHref(item) : null;
        const useLink = Boolean(href);
        const rowClassName = [
          'st-row',
          reorderMode ? 'is-draggable' : '',
          dragIndex === index ? 'is-dragging' : '',
          overIndex === index && dragIndex !== index ? 'is-over' : '',
          locked ? 'is-locked' : '',
        ].filter(Boolean).join(' ');
        const rowBody = (
          <>
            {columns.map((column) => (
              <div key={column.key} className={`st-cell ${column.className || ''}`.trim()}>
                {column.render ? column.render(item, index) : item[column.key]}
              </div>
            ))}
            {reorderMode ? (
              <div className="st-cell st-cell-actions">
                <button
                  type="button"
                  className="st-icon-btn"
                  disabled={index === 0}
                  onClick={(event) => {
                    event.stopPropagation();
                    moveRow(index, index - 1);
                  }}
                  aria-label={t('stMoveUp')}
                  title={t('stMoveUp')}
                >
                  <FiChevronUp />
                </button>
                <button
                  type="button"
                  className="st-icon-btn"
                  disabled={index === items.length - 1}
                  onClick={(event) => {
                    event.stopPropagation();
                    moveRow(index, index + 1);
                  }}
                  aria-label={t('stMoveDown')}
                  title={t('stMoveDown')}
                >
                  <FiChevronDown />
                </button>
              </div>
            ) : null}
          </>
        );

        if (useLink) {
          return (
            <Link
              key={getRowKey(item, index)}
              to={href}
              className={rowClassName}
            >
              {rowBody}
            </Link>
          );
        }

        return (
          <div
            key={getRowKey(item, index)}
            className={rowClassName}
            draggable={reorderMode}
            onDragStart={(event) => handleDragStart(index, event)}
            onDragOver={(event) => handleDragOver(index, event)}
            onDrop={(event) => handleDrop(index, event)}
            onDragEnd={handleDragEnd}
            onClick={() => {
              if (reorderMode) return;
              if (locked) return;
              if (onRowClick) onRowClick(item);
            }}
          >
            {rowBody}
          </div>
        );
      })}
    </div>
  );
}
